'use client'

import { createContext, useContext, useId, type AriaAttributes, type ReactNode } from 'react'
import type { TestIdProps } from '../internal/test-id'

/**
 * The wiring every form control needs and nobody remembers to write.
 *
 * A label has to point at its control, an error has to be announced when the
 * control is focused, and an invalid control has to say so to assistive
 * technology. Doing that by hand means three ids and two ARIA attributes per
 * field, which is why it is usually wrong.
 *
 * Field owns those ids and hands them to whatever control sits inside it, so a
 * consumer writes markup and gets the accessibility for free:
 *
 *   <Field label="Company name" error={errors.name?.message}>
 *       <Input {...register('name')} />
 *   </Field>
 *
 * Copy is always a prop. The package ships no strings, so a field works
 * identically in an app with next-intl and in the design system app without it.
 */
type FieldContextValue = {
    /** Undefined when the control labels itself, so it keeps its own id. */
    controlId: string | undefined
    describedBy: string | undefined
    isInvalid: boolean
    isRequired: boolean
}

const FieldContext = createContext<FieldContextValue | null>(null)

/**
 * Read the surrounding Field, if there is one.
 *
 * Controls are usable standalone, so this returns null rather than throwing.
 */
export const useFieldControl = (): FieldContextValue | null => useContext(FieldContext)

/**
 * What a control inherits from the Field around it, ready to spread.
 *
 * Nine controls each wrote these four lines and two of them had already
 * drifted: `Input` and `Textarea` spread the caller's props *after* the wiring
 * rather than before, so a caller handing over `id={undefined}` silently wiped
 * the association the Field had just made and the label pointed at nothing.
 *
 * Spread this after the caller's props. A value the caller gave is preserved by
 * the fallbacks below, so the later spread cannot overwrite an explicit choice,
 * and the order stops being a decision each control gets to make on its own.
 *
 * Required is announced, never enforced by the browser. The native `required`
 * attribute makes the browser validate the form before React sees the submit.
 * The browser blocks the submit. It then shows its own bubble, anchored to a
 * control the user often cannot see, so the field's own error never renders.
 * `aria-required` tells assistive technology the same thing. It leaves the
 * validation to the form library.
 */
export const useFieldControlProps = (props: {
    id?: string
    'aria-describedby'?: string
    // The full ARIA union, `grammar` and `spelling` included, so the hook takes
    // whatever a control's own HTML attribute type allows.
    'aria-invalid'?: AriaAttributes['aria-invalid']
    'aria-required'?: AriaAttributes['aria-required']
}) => {
    const field = useFieldControl()

    return {
        id: props.id ?? field?.controlId,
        'aria-describedby': props['aria-describedby'] ?? field?.describedBy,
        'aria-invalid': props['aria-invalid'] ?? field?.isInvalid,
        'aria-required': props['aria-required'] ?? (field?.isRequired || undefined)
    }
}

export type FieldProps = TestIdProps & {
    /** Visible label text. Required: an unlabelled control is a bug, not a style. */
    label: string
    children: ReactNode
    /** Guidance shown under the control. Announced with it. */
    hint?: string
    /** Validation message. Its presence is what marks the field invalid. */
    error?: string
    isRequired?: boolean
    /** Visually hides the label while keeping it available to screen readers. */
    isLabelHidden?: boolean
    /**
     * The control carries its own visible label, as Checkbox and Switch do.
     *
     * Field then names a group rather than the control, because two labels
     * pointing at one control concatenate into a single confusing accessible
     * name: "Mandate I agree to the direct debit mandate".
     */
    isControlLabelled?: boolean
}

export const Field = ({
    label,
    children,
    hint,
    error,
    isRequired = false,
    isLabelHidden = false,
    isControlLabelled = false,
    'data-testid': testId
}: FieldProps) => {
    const id = useId()
    const controlId = `${id}-control`
    const labelId = `${id}-label`
    const hintId = `${id}-hint`
    const errorId = `${id}-error`

    // Both are announced when both are present, error first, because the reason
    // the control was rejected matters more than the guidance it ignored.
    const describedBy = [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ') || undefined

    const labelClassName = isLabelHidden ? 'sr-only' : 'text-foreground text-aria-sm leading-none font-medium'

    const labelContent = (
        <>
            {label}
            {isRequired ? (
                <span aria-hidden className="text-foreground-danger ml-0.5">
                    *
                </span>
            ) : null}
        </>
    )

    const body = (
        <>
            {children}

            {hint ? (
                <p id={hintId} className="text-foreground-muted text-aria-xs leading-normal">
                    {hint}
                </p>
            ) : null}

            {error ? (
                <p id={errorId} className="text-foreground-danger text-aria-xs leading-normal">
                    {error}
                </p>
            ) : null}
        </>
    )

    return (
        <FieldContext.Provider
            value={{
                controlId: isControlLabelled ? undefined : controlId,
                describedBy,
                isInvalid: Boolean(error),
                isRequired
            }}
        >
            {/* A group gets more room under its label than a single control
                does: the label names a set of options rather than sitting on
                one line above one input, and 1.5 leaves it reading as the first
                option's caption. */}
            {isControlLabelled ? (
                <div role="group" data-testid={testId} aria-labelledby={labelId} className="flex flex-col gap-3">
                    <span id={labelId} className={labelClassName}>
                        {labelContent}
                    </span>
                    {body}
                </div>
            ) : (
                <div data-testid={testId} className="flex flex-col gap-1.5">
                    <label htmlFor={controlId} className={labelClassName}>
                        {labelContent}
                    </label>
                    {body}
                </div>
            )}
        </FieldContext.Provider>
    )
}
